import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  FilmIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  TagIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { useContent } from '../hooks/useContent';
import { contentApi, campaignApi } from '../services/api';
import type { Content, Group } from '../types/content';
import type { Campaign } from '../types/campaign';

type ViewMode = 'card' | 'list';

// Ghost Card Component for loading state
const GhostCard: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse transition-colors">
    {/* Thumbnail skeleton */}
    <div className="h-48 bg-gray-200 dark:bg-gray-700"></div>
    
    {/* Content skeleton */}
    <div className="p-4">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-4 w-3/4"></div>
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-4">
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
        <div className="flex space-x-2">
          <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    </div>
  </div>
);

// Ghost List Item Component for loading state
const GhostListItem: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 px-4 py-4 sm:px-6 animate-pulse transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4 flex-1">
        <div className="w-16 h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-1/3"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-1/2"></div>
          <div className="flex space-x-4">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
          </div>
        </div>
      </div>
      <div className="flex space-x-2">
        <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>
  </div>
);

const Content: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterGroup, setFilterGroup] = useState<string>('all');
  const [filterTranscription, setFilterTranscription] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [activeCampaigns, setActiveCampaigns] = useState<Campaign[]>([]);
  const [contentAdSlots, setContentAdSlots] = useState<Record<string, number>>({});

  const {
    content: contentList,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    setPage,
    archiveContent,
    restoreContent,
    refreshContent
  } = useContent({
    page: 1,
    limit: 21,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    showArchived: false
  });

  // Fetch available groups and active campaigns on component mount
  useEffect(() => {
    const fetchGroups = async () => {
      setGroupsLoading(true);
      try {
        const response = await contentApi.getGroups();
        console.log('🔍 DEBUG: Groups response:', response);
        const groups = (response as any)?.data || response || [];
        
        if (Array.isArray(groups)) {
          const formattedGroups = groups.map((group: string | Group) => {
            if (typeof group === 'string') {
              return { id: group, title: group };
            }
            return group;
          });
          setAvailableGroups(formattedGroups);
        } else {
          console.warn('⚠️ Groups response is not an array:', groups);
          setAvailableGroups([]);
        }
      } catch (error) {
        console.error('❌ Error fetching groups:', error);
        setAvailableGroups([]);
      } finally {
        setGroupsLoading(false);
      }
    };

    const fetchActiveCampaigns = async () => {
      try {
        const response = await campaignApi.list({ 
          status: 'active',
          limit: 100 // Get active campaigns for dropdown
        });
        const campaigns = response?.campaigns || [];
        setActiveCampaigns(campaigns);
      } catch (error) {
        console.error('❌ Error fetching active campaigns:', error);
        setActiveCampaigns([]);
      }
    };

    fetchGroups();
    fetchActiveCampaigns();
  }, []);

  // Update filters when local filter states change
  useEffect(() => {
    setFilters({
      page: 1, // Reset to page 1 when filters change
      limit: 21,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      showArchived: filters.showArchived || false,
      search: searchTerm || undefined,
      contentType: filterType === 'all' ? undefined : (filterType as any),
      status: filterStatus === 'all' ? undefined : (filterStatus as any),
      groupName: filterGroup === 'all' ? undefined : filterGroup,
      transcriptionStatus: filterTranscription === 'all' ? undefined : (filterTranscription as any)
    });
  }, [searchTerm, filterType, filterStatus, filterGroup, filterTranscription, filters.showArchived, setFilters]);

  const formatDuration = (seconds: number, contentType: string): string => {
    if (contentType === 'live') return 'Canlı';
    if (seconds === 0) return '0:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'draft':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      case 'processing':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'pending':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'transcription_failed':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'error':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'draft':
        return 'Taslak';
      case 'processing':
        return 'İşleniyor';
      case 'pending':
        return 'Bekliyor';
      case 'transcription_failed':
        return 'Transkripsiyon Hatası';
      case 'error':
        return 'Hata';
      default:
        return status;
    }
  };

  const getTypeText = (type: string): string => {
    switch (type) {
      case 'movie':
        return 'Film';
      case 'series':
        return 'Dizi';
      case 'documentary':
        return 'Belgesel';
      case 'live':
        return 'Canlı';
      default:
        return type;
    }
  };

  const getTranscriptionStatusText = (status?: string): string => {
    if (!status) return 'Yok';
    switch (status) {
      case 'completed':
        return 'Tamamlandı';
      case 'failed':
        return 'Başarısız';
      case 'pending':
        return 'Bekliyor';
      case 'audio_extraction_processing':
      case 'audio_extracted':
      case 'transcribe_processing':
      case 'transcribe_completed':
        return 'İşleniyor';
      default:
        return status;
    }
  };

  const getTranscriptionStatusColor = (status?: string): string => {
    if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'audio_extraction_processing':
      case 'audio_extracted':
      case 'transcribe_processing':
      case 'transcribe_completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Delete content function
  const handleDeleteContent = async (content: Content) => {
    if (!window.confirm(`"${content.title}" içeriğini arşivlemek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await archiveContent(content.id);
      alert('İçerik başarıyla arşivlendi.');
    } catch (error) {
      console.error('Delete content error:', error);
      alert('İçerik arşivlenirken bir hata oluştu.');
    }
  };

  // Restore content function
  const handleRestoreContent = async (content: Content) => {
    if (!window.confirm(`"${content.title}" içeriğini geri yüklemek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await restoreContent(content.id);
      alert('İçerik başarıyla geri yüklendi.');
    } catch (error) {
      console.error('Restore content error:', error);
      alert('İçerik geri yüklenirken bir hata oluştu.');
    }
  };

  const handleShowArchivedToggle = (showArchived: boolean) => {
    setFilters({
      ...filters,
      showArchived,
      page: 1 // Reset to first page when toggling archived
    });
  };

  // Calculate active ad slots for content based on campaign keyword targeting
  const calculateActiveAdSlots = (content: Content): number => {
    if (!activeCampaigns.length || !content.tags || !content.tags.length) {
      return 0;
    }

    // Count campaigns that target this content through keyword matching
    const matchingCampaigns = activeCampaigns.filter(campaign => {
      // Check if campaign has target keywords
      if (!campaign.targetKeywords || !campaign.targetKeywords.length) {
        return false;
      }

      // Check if any campaign keywords match content tags
      return campaign.targetKeywords.some(targetKeyword => {
        if (targetKeyword.status !== 'active') {
          return false;
        }
        
        // Simple keyword matching (case-insensitive)
        const keyword = targetKeyword.keyword?.toLowerCase() || '';
        return content.tags!.some(tag => 
          tag.toLowerCase().includes(keyword) || 
          keyword.includes(tag.toLowerCase())
        );
      });
    });

    return matchingCampaigns.length;
  };

  // Calculate ad slots for all content when campaigns or content changes
  useEffect(() => {
    if (contentList.length > 0 && activeCampaigns.length > 0) {
      const newContentAdSlots: Record<string, number> = {};
      contentList.forEach(content => {
        newContentAdSlots[content.id] = calculateActiveAdSlots(content);
      });
      setContentAdSlots(newContentAdSlots);
    } else if (contentList.length > 0) {
      // Reset to 0 if no active campaigns
      const newContentAdSlots: Record<string, number> = {};
      contentList.forEach(content => {
        newContentAdSlots[content.id] = 0;
      });
      setContentAdSlots(newContentAdSlots);
    }
  }, [contentList, activeCampaigns]);

  // Card view component
  const ContentCard: React.FC<{ content: Content }> = ({ content }) => (
    <Link 
      to={`/content/${content.id}`}
      className="block bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg transition-colors hover:shadow-md cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="relative h-48 bg-gray-200 dark:bg-gray-700">
        {content.thumbnailUrl ? (
          <img
            src={content.thumbnailUrl}
            alt={content.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FilmIcon className="h-16 w-16 text-gray-400" />
          </div>
        )}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(content.status)}`}>
            {getStatusText(content.status)}
          </span>
          {content.archivedAt && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              Arşivlenmiş
            </span>
          )}
        </div>
        <div className="absolute bottom-2 left-2">
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-black bg-opacity-75 text-white">
            <ClockIcon className="mr-1 h-3 w-3" />
            {formatDuration(content.durationSeconds || 0, content.contentType)}
          </span>
        </div>
      </div>

      {/* Content Info */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
              {content.title}
            </h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                {content.description || 'Açıklama bulunmuyor'}
              </p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <TagIcon className="mr-1 h-4 w-4" />
              {getTypeText(content.contentType)}
            </span>
            <span className="flex items-center">
              <EyeIcon className="mr-1 h-4 w-4" />
              {(content.views || 0).toLocaleString()}
            </span>
          </div>
          <span className="text-blue-600 font-medium">
            {contentAdSlots[content.id] || 0} reklam slotu
          </span>
        </div>

        {/* Transcription Status */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Transkripsiyon:</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTranscriptionStatusColor(content.transcriptionStatus)}`}>
            {getTranscriptionStatusText(content.transcriptionStatus)}
          </span>
        </div>

        {content.contentType === 'series' && (
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Bölüm {content.episodeNumber} / {content.totalEpisodes}
          </div>
        )}

        {/* Tags */}
        {content.tags && content.tags.length > 0 && (
          <div className="mt-2">
            <div className="flex flex-wrap gap-1">
              {content.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between">
          <span className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700">
            <EyeIcon className="mr-1 h-3 w-3" />
            Detay
          </span>
          <div className="flex items-center space-x-2" onClick={(e) => e.preventDefault()}>
            <Link
              to={`/content/${content.id}/edit`}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Düzenle"
              onClick={(e) => e.stopPropagation()}
            >
              <PencilIcon className="h-4 w-4" />
            </Link>
            {content.archivedAt ? (
              <button
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                title="Geri Yükle"
                onClick={() => handleRestoreContent(content)}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>
            ) : (
              <button
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Arşivle"
                onClick={() => handleDeleteContent(content)}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  // List view component
  const ContentListItem: React.FC<{ content: Content }> = ({ content }) => (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
      <div className="px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            {/* Thumbnail */}
            <div className="flex-shrink-0 w-16 h-12 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
              {content.thumbnailUrl ? (
                <img
                  src={content.thumbnailUrl}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FilmIcon className="h-6 w-6 text-gray-400" />
                </div>
              )}
            </div>

            {/* Content Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {content.title}
                </h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(content.status)}`}>
                  {getStatusText(content.status)}
                </span>
                {content.archivedAt && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Arşivlenmiş
                  </span>
                )}
              </div>
                             <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
                 {content.description || 'Açıklama bulunmuyor'}
               </p>
              <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center">
                  <TagIcon className="mr-1 h-3 w-3" />
                  {getTypeText(content.contentType)}
                </span>
                <span className="flex items-center">
                  <ClockIcon className="mr-1 h-3 w-3" />
                  {formatDuration(content.durationSeconds || 0, content.contentType)}
                </span>
                <span className="flex items-center">
                  <EyeIcon className="mr-1 h-3 w-3" />
                  {(content.views || 0).toLocaleString()}
                </span>
                <span>{content.adSlots || 0} reklam slotu</span>
                <span className="flex items-center">
                  <span>Transkripsiyon:</span>
                  <span className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTranscriptionStatusColor(content.transcriptionStatus)}`}>
                    {getTranscriptionStatusText(content.transcriptionStatus)}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <Link
              to={`/content/${content.id}`}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Detay"
            >
              <EyeIcon className="h-4 w-4" />
            </Link>
            <Link
              to={`/content/${content.id}/edit`}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Düzenle"
            >
              <PencilIcon className="h-4 w-4" />
            </Link>
            {content.archivedAt ? (
              <button
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                title="Geri Yükle"
                onClick={() => handleRestoreContent(content)}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>
            ) : (
              <button
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Arşivle"
                onClick={() => handleDeleteContent(content)}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">İçerik Yönetimi</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Video içeriklerinizi yönetin ve reklam slotlarını düzenleyin
          </p>
        </div>
        <Link
          to="/content/add"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Yeni İçerik
        </Link>
      </div>

      {/* Filters and View Toggle */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 transition-colors">
        {/* First Row: Search and Filters */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-6 mb-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Arama
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="İçerik ara..."
              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              İçerik Türü
            </label>
            <select
              id="type-filter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tümü</option>
              <option value="movie">Film</option>
              <option value="series">Dizi</option>
              <option value="documentary">Belgesel</option>
              <option value="live">Canlı</option>
            </select>
          </div>
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Durum
            </label>
            <select
              id="status-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="draft">Taslak</option>
              <option value="pending">Bekliyor</option>
              <option value="processing">İşleniyor</option>
              <option value="transcription_failed">Transkripsiyon Hatası</option>
              <option value="error">Hata</option>
            </select>
          </div>
          <div>
            <label htmlFor="group-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Grup
            </label>
            <select
              id="group-filter"
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              disabled={groupsLoading}
            >
              <option value="all">Tümü</option>
              {Array.isArray(availableGroups) && availableGroups.map((group: any) => (
                <option key={group.id || group} value={group.id || group}>
                  {group.title || group}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="transcription-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Transkripsiyon
            </label>
            <select
              id="transcription-filter"
              value={filterTranscription}
              onChange={(e) => setFilterTranscription(e.target.value)}
              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tümü</option>
              <option value="none">Yok</option>
              <option value="completed">Tamamlandı</option>
              <option value="processing">İşleniyor</option>
              <option value="failed">Başarısız</option>
            </select>
          </div>
          <div>
            <label htmlFor="archive-toggle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Arşiv Durumu
            </label>
            <div className="mt-1">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  id="archive-toggle"
                  checked={filters.showArchived || false}
                  onChange={(e) => handleShowArchivedToggle(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {filters.showArchived ? 'Arşivlenmiş İçerikleri Göster' : 'Aktif İçerikleri Göster'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Second Row: View Mode Toggle */}
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Toplam {pagination.total} içerik
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">Görünüm:</span>
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-2 text-xs font-medium rounded-l-md border ${
                  viewMode === 'card'
                    ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900 dark:border-blue-400 dark:text-blue-300'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                } transition-colors`}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-xs font-medium rounded-r-md border-t border-b border-r ${
                  viewMode === 'list'
                    ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900 dark:border-blue-400 dark:text-blue-300'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                } transition-colors`}
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Hata</h3>
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
              <button
                onClick={refreshContent}
                className="mt-2 text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
              >
                Tekrar dene
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content List */}
      {loading ? (
        viewMode === 'card' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <GhostCard key={index} />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <GhostListItem key={index} />
            ))}
          </div>
        )
      ) : contentList.length === 0 ? (
        <div className="text-center py-12">
          <FilmIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">İçerik bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Arama kriterlerinize uygun içerik bulunamadı.
          </p>
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {contentList.map((content: Content) => (
            <ContentCard key={content.id} content={content} />
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          {contentList.map((content: Content) => (
            <ContentListItem key={content.id} content={content} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Önceki
            </button>
            <button
              onClick={() => setPage(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                {' - '}
                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                {' / '}
                <span className="font-medium">{pagination.total}</span>
                {' sonuç gösteriliyor'}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Önceki
                </button>
                {(() => {
                  const currentPage = pagination.page;
                  const totalPages = pagination.totalPages;
                  const maxButtons = 5;
                  
                  let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  // Adjust start if we're near the end
                  if (endPage - startPage + 1 < maxButtons) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }
                  
                  return pages.map((page) => (
                    <button
                      key={page}
                      onClick={() => setPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                        page === pagination.page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:border-blue-400 dark:text-blue-300'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-600'
                      }`}
                    >
                      {page}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => setPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Sonraki
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;